<template>
  <v-card flat>
    <!-- Confirm modal -->
    <ConfirmationModal
      ref="confirmationModal"
      :title="confirmationTitle"
      :subtitle="confirmationSubtitle"
      :message="confirmationMessage"
      confirmColor="error darken-1"
    />
    
    <v-row align="center" justify="space-between">
      <v-col cols="12" md="9">
        <Header
          :title="`Contact Information`"
          subtitle="Manage the contact details of the customer"
        />
      </v-col>

      <v-col cols="12" md="3" class="text-md-right">
        <v-btn @click="openForm(null)"  text outlined class="shadow-md text-capitalize"
          >
          <v-icon left>mdi-plus</v-icon>
          Create</v-btn
        >
      </v-col>
    </v-row>

    <!-- Empty state -->
    <div v-if="!contacts || (contacts && !contacts.length)" class="text-center">
      <NoData />
      <p class="mt-5 font-weight-bold">No contacts found</p>
      <v-btn
        @click="openForm(null)"
        depressed
        text
        outlined
        color="primary"
        min-height="38"
        class="text-capitalize shadow-md"
        >
        <v-icon left>mdi-plus</v-icon>
        Create New</v-btn
      >
    </div>
    
    <div v-else class="mt-5">
      <template v-for="(item, i) in contacts">
        <v-card :key="`contact-${i}`" flat outlined class="mb-2">
          <v-card-text>
            <v-row align="center" justify="space-between">
              <v-col cols="12" md="7">
                <div class="d-flex flex-column">
                  <span class="font-weight-medium">{{ item.number }} </span>
                  <span class="text-body-2 text--secondary">{{
                    item.type
                  }}</span>
                </div>
              </v-col>
              <v-col class="text-md-right">
                <v-btn
                  small
                  @click="
                    openForm(item, i);
                  "
                  text
                  outlined
                  class="shadow mr-2 text-capitalize"
                  >Edit</v-btn
                >
                <v-btn
                  @click="deleteContact(item, i)"
                  small
                  depressed
                  color="error"
                  class="shadow text-capitalize"
                  >Delete</v-btn
                >
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </template>
    </div>
    <ModalFormLayout
      @submit="onSubmit"
      @cancel="onClose"
      ref="contactModal"
      :options="modalOptions"
    >
      <template #content>
        <ValidationObserver ref="observer">
          <v-row class="mt-2" dense>
            <v-col cols="12">
              <ValidationProvider
                name="Type"
                rules="required"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-select
                  :items="['Home', 'Cell', 'Work']"
                  dense
                  outlined
                  label="Type"
                  :error-messages="errors"
                  v-model="details.type"
                >
                  <template v-slot:label>
                    Type
                    <span class="red--text">*</span>
                  </template>
                </v-select>
              </ValidationProvider>
            </v-col>
            <v-col cols="12">
              <ValidationProvider
                name="Number"
                rules="required|telephone"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-text-field
                  dense
                  outlined
                  label="Number"
                  :error-messages="errors"
                  v-model="details.number"
                >
                  <template v-slot:label>
                    Number
                    <span class="red--text">*</span>
                  </template>
                </v-text-field>
              </ValidationProvider>
            </v-col>
          </v-row>
        </ValidationObserver>
      </template>
    </ModalFormLayout>
  </v-card>
</template>

<script>
import pageMixin from '@/mixins/page.mixin';
import Header from './Header.vue';

export default {
  components: {
    Header
  },
  mixins: [pageMixin],
  props: {
    outlined : {
      type: Boolean,
      default: true, 
    },
    contacts: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      details: {},
      isEditing: false,
      editIndex: -1,

      // Confirmation modal options
      confirmationTitle: '',
      confirmationMessage: '',
      confirmationSubtitle: '',

      // Modal options
      modalOptions: {
        title: 'Add a contact',
        subtitle: 'Enter the values and click submit',
        submitButtonText: 'Submit',
        maxWidth: '400px',
        width: '400px',
        color: 'white',
        flat: true
      }
    };
  },
  emits: ['deleteContact', 'update'],
  methods: {
    
    // Open the form for creating/editing a new source
    openForm(item, index = -1) {
      this.modalOptions.title = 'Create a contact';
      this.modalOptions.subtitle = 'A new contact will be created';
      
      this.editIndex = index;
      if (index > -1) {
        this.details = {...item};
        this.isEditing = true;
        this.modalOptions.title = `Update contact`;
        this.modalOptions.subtitle = `editing contact`;
      }
      this.$refs.contactModal.open();
    },
    
    onClose() {
      this.$refs.observer?.reset();
      this.details = {};
      this.isEditing = false;
      this.editIndex = null;
    },
    
    async onSubmit() {
      const valid = await this.$refs.observer.validate();
      if (!valid) return;

      let contacts = structuredClone(this.contacts);
      if (this.editIndex > -1) {
        contacts = contacts.map((e, i) => i == this.editIndex ? this.details : e );
      } else {
       contacts.push(this.details);
      }
      
      this.onClose();
      this.$emit('update', contacts);
      this.$refs.contactModal.close();
    },
    
    deleteContact(item, index = -1) {
      this.confirmationTitle = 'Confirm Delete';
      this.confirmationMessage = 'Are you sure you want to delete this record?';

      this.editIndex = index;
      this.$refs.confirmationModal.show(async () => {
        let contacts = structuredClone(this.contacts);
        contacts = contacts.filter((e, i) => i !== this.editIndex );
      
        this.$emit('update', contacts);
      });
    }
  }
};
</script>
